import { useState } from "react";
import PropTypes from "prop-types";

import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import SoftBox from "components/SoftBox";
import { NavLink } from "react-router-dom";

import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "cxfcomponents/Sidenav/styles/sidenavCollapse";

import { useSoftUIController } from "context";
import ProtectedPage from "authentication/ProtectedPage";

function SidenavCollapse({
  icon,
  name,
  active = false,
  noCollapse = false,
  isMinified = false,
  open = false,
  color = "info",
  onClick,
  collapse,
  itemName,
  themeMode,
  flush = false,
  ...rest
}) {
  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav, sidenavColor } = controller;

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const ConditionalWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

  const handleClick = (event) => {
    if (noCollapse || !collapse?.length) {
      onClick?.(event);
      return;
    }

    if (miniSidenav) {
      onClick?.(event);
      setAnchorEl((prev) => (prev ? null : event.currentTarget));
      return;
    }

    onClick?.(event);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const renderMenuItems = (routes, closeAfterClick = false) =>
    routes?.map(({ name, href, route, key, visuallyHidden, isComponentDown }) => {
      if (visuallyHidden || isComponentDown === "hidden") return null;

      const commonSx = {
        borderRadius: "4px",
        fontSize: "0.92rem",
        lineHeight: 1.4,
        minHeight: 32,
        px: 1.5,
        fontWeight: 400,
        transition: "all 0.1s ease-in-out",
        "&:hover": {
          fontWeight: 400,
          background: themeMode === "dark" ? "rgb(0, 0, 0) !important" : "rgba(34, 39, 44, 0.09) !important",
          transition: "all 0.1s ease-in-out",
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          background: themeMode === "dark" ? "rgb(0, 0, 0) !important" : "rgba(34, 39, 44, 0.09) !important",
          color: themeMode === "dark" ? "#fff !important" : "#000 !important",
          fontWeight: 400,
        }
      };

      if (href) {
        return (
          <ProtectedPage key={key} href={href}>
            <MenuItem
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAfterClick ? handleCloseMenu : undefined}
              selected={key === itemName}
              sx={commonSx}
            >
              {name}
            </MenuItem>
          </ProtectedPage>
        );
      }

      return (
        <ProtectedPage key={key} route={route}>
          <MenuItem
            component={NavLink}
            to={route}
            onClick={closeAfterClick ? handleCloseMenu : undefined}
            selected={key === itemName}
            sx={commonSx}
          >
            {name}
          </MenuItem>
        </ProtectedPage>
      );
    });

  return (
    <>
      <ListItem component="li" disablePadding>
        <SoftBox
          {...{ ...rest, color }}
          onClick={handleClick}
          sx={(theme) => ({
            ...collapseItem(theme, { active, transparentSidenav }),
            ...(flush && {
              width: "calc(100% - 24px)",
              minHeight: 44,
              margin: "2px 12px",
              padding: "4px 8px",
              borderRadius: "6px",
              background: "transparent",
              "&:hover": {
                background: themeMode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(34, 39, 44, 0.05)",
              },
            }),
            ...(flush && isMinified && {
              width: 44,
              margin: "2px auto",
              padding: 0,
              justifyContent: "center",
            }),
            cursor: "pointer",
            userSelect: "none",
            transition: "all 0.1s ease-in-out",
          })}
        >
          <ConditionalWrapper
            condition={isMinified}
            wrapper={(children) => (
              <Tooltip title={name} placement="right">
                {children}
              </Tooltip>
            )}
          >
            <ListItemIcon
              sx={(theme) => ({
                ...collapseIconBox(theme, {
                  active,
                  transparentSidenav,
                  sidenavColor,
                }),
                ...(flush && isMinified && {
                  minWidth: 32,
                  minHeight: 32,
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }),
              })}
            >
              {typeof icon === "string" ? (
                <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
              ) : (
                icon
              )}
            </ListItemIcon>
          </ConditionalWrapper>

          <ListItemText
            primary={name}
            sx={(theme) => ({
              ...collapseText(theme, { miniSidenav, transparentSidenav, active }),
              ...(flush && isMinified && { display: "none" }),
              fontSize: flush ? "0.94rem" : "0.97rem",
              lineHeight: 1.1,
              fontWeight: flush && active ? 500 : 400,
              "& span": {
                fontSize: flush ? "0.94rem" : "0.97rem",
                lineHeight: 1.1,
                fontWeight: flush && active ? 500 : 400,
              },
            })}
          />
        </SoftBox>
      </ListItem>

      {miniSidenav && collapse?.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                boxShadow:
                  themeMode === "dark"
                    ? "0px 4px 24px rgba(0, 0, 0, 0.45) !important"
                    : "0px 4px 24px rgba(0, 0, 0, 0.12) !important",
              },
            },
          }}
        >
          {renderMenuItems(collapse, true)}
        </Menu>
      )}

      {!miniSidenav && collapse?.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <SoftBox component="ul" sx={{ listStyle: "none", m: 0, pl: flush ? 0 : 3 }}>
            {collapse.map(({ name, href, route, key, visuallyHidden, isComponentDown }) => {
              if (visuallyHidden || isComponentDown === "hidden") return null;

              const commonSx = {
                borderRadius: "4px",
                fontSize: "0.92rem",
                lineHeight: 1.1,
                minHeight: 32,
                ml: 1.5,
                px: 2,
                color: themeMode === "dark" ? "#cccccc" : "#000",
                fontWeight: 400,
                transition: "all 0.1s ease-in-out",
                "&:hover": {
                  color: themeMode === "dark" ? "#fff" : "#000",
                  fontWeight: 800,
                  background: "none !important",
                  transition: "all 0.1s ease-in-out",
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  background: 'none !important',
                  color: themeMode === "dark" ? "#fff !important" : "#000 !important",
                  fontWeight: 800,
                },
                ...(flush && {
                  ml: 4.5,
                  my: "2px",
                  width: "calc(100% - 48px)",
                  minHeight: 42,
                  pl: "30px",
                  pr: 1,
                  fontSize: "0.88rem",
                  borderLeft: "3px solid transparent",
                  borderRadius: "6px",
                  "&:hover": {
                    color: themeMode === "dark" ? "#fff" : "#000",
                    fontWeight: 400,
                    background: themeMode === "dark" ? "rgba(255, 255, 255, 0.05) !important" : "rgba(34, 39, 44, 0.05) !important",
                  },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    background: themeMode === "dark" ? "#202a38 !important" : "#e7ebf7 !important",
                    borderLeftColor: "#7443ff",
                    color: themeMode === "dark" ? "#fff !important" : "#1e283e !important",
                    fontWeight: 600,
                  },
                }),
              };

              if (href) {
                return (
                  <ProtectedPage key={key} href={href}>
                    <li>
                      <MenuItem
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        selected={key === itemName}
                        sx={commonSx}
                      >
                        {name}
                      </MenuItem>
                    </li>
                  </ProtectedPage>
                );
              }

              return (
                <ProtectedPage key={key} route={route}>
                  <li>
                    <MenuItem
                      component={NavLink}
                      to={route}
                      selected={key === itemName}
                      sx={commonSx}
                    >
                      {name}
                    </MenuItem>
                  </li>
                </ProtectedPage>
              );
            })}
          </SoftBox>
        </Collapse>
      )}
    </>
  );
}

SidenavCollapse.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  open: PropTypes.bool,
  isMinified: PropTypes.bool,
  onClick: PropTypes.func,
  collapse: PropTypes.array,
  itemName: PropTypes.string,
  themeMode: PropTypes.string,
  flush: PropTypes.bool,
};

export default SidenavCollapse;
